import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage, functions } from './firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAdminView } from './contexts/AdminViewContext';
import './BadgesAdminPage.css';

const BadgesAdminPage = ({ user }) => {
    const navigate = useNavigate();
    const { isAdmin } = useAdminView();
    const [badges, setBadges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [editingBadgeId, setEditingBadgeId] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    
    // Form state
    const [badgeName, setBadgeName] = useState('');
    const [badgeSize, setBadgeSize] = useState(15); // Default 15%
    const [badgeImage, setBadgeImage] = useState(null);
    const [badgeImagePreview, setBadgeImagePreview] = useState(null);
    const [conditionType, setConditionType] = useState('distance');
    const [distanceThreshold, setDistanceThreshold] = useState(50); // km
    const [activityType, setActivityType] = useState(''); // 'ride' or 'run', empty means all
    const [locationCity, setLocationCity] = useState('');
    const [locationState, setLocationState] = useState('');
    const [locationCountry, setLocationCountry] = useState('');
    const [styleVersions, setStyleVersions] = useState({});
    const [availableStyles, setAvailableStyles] = useState([]);
    const [showStyleVersions, setShowStyleVersions] = useState(false);
    
    // Check if user is admin
    useEffect(() => {
        if (!user || !isAdmin) {
            navigate('/');
            return;
        }
    }, [user, isAdmin, navigate]);
    
    // Load existing badges and available styles
    useEffect(() => {
        loadBadges();
        loadAvailableStyles();
    }, []);
    
    // Auto-hide notification after 3 seconds
    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                setNotification({ ...notification, show: false });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notification.show]);
    
    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
    };
    
    const loadBadges = async () => {
        setIsLoading(true);
        try {
            const badgesSnapshot = await getDocs(collection(db, 'badges'));
            const badgesList = badgesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setBadges(badgesList);
        } catch (error) {
            console.error('Error loading badges:', error);
        } finally {
            setIsLoading(false);
        }
    };
    
    const loadAvailableStyles = async () => {
        try {
            const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
            const result = await getTemplatesFunc();
            if (result.data && result.data.templates) {
                const styles = result.data.templates.map(template => ({
                    id: template.id,
                    name: template.name
                }));
                // Add default style at the beginning
                setAvailableStyles([{ id: 'default', name: 'Default Style' }, ...styles]);
            }
        } catch (error) {
            console.error('Error loading available styles:', error);
        }
    };
    
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBadgeImage(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setBadgeImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    
    const handleEditBadge = (badge) => {
        // Set the form fields with the badge data
        setEditingBadgeId(badge.id);
        setBadgeName(badge.name);
        setBadgeSize(badge.size || 6);
        setBadgeImagePreview(badge.imageUrl);
        setConditionType(badge.conditionType);
        setStyleVersions(badge.styleVersions || {});
        
        if (badge.conditionType === 'distance') {
            setDistanceThreshold(badge.distanceThreshold / 1000); // Convert to km
            setActivityType(badge.activityType || '');
        } else if (badge.conditionType === 'location') {
            setLocationCity(badge.locationCity || '');
            setLocationState(badge.locationState || '');
            setLocationCountry(badge.locationCountry || '');
        }
        
        // Scroll to the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    
    const handleCancelEdit = () => {
        setEditingBadgeId(null);
        setBadgeName('');
        setBadgeSize(15);
        setBadgeImage(null);
        setBadgeImagePreview(null);
        setConditionType('distance');
        setDistanceThreshold(50);
        setActivityType('');
        setLocationCity('');
        setLocationState('');
        setLocationCountry('');
        setStyleVersions({});
        setShowStyleVersions(false);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!badgeName || (!badgeImage && !editingBadgeId)) {
            showNotification('Please provide a badge name and image', 'error');
            return;
        }
        
        setIsUploading(true);
        
        try {
            let imageUrl;
            
            // If there's a new image, upload it
            if (badgeImage) {
                const timestamp = Date.now();
                const filename = `badges/${timestamp}_${badgeImage.name}`;
                const storageRef = ref(storage, filename);
                await uploadBytes(storageRef, badgeImage);
                imageUrl = await getDownloadURL(storageRef);
            } else if (editingBadgeId) {
                // Keep the existing image URL when editing without a new image
                imageUrl = badgeImagePreview;
            }
            
            // Prepare badge data
            const badgeData = {
                name: badgeName,
                imageUrl,
                size: badgeSize,
                conditionType,
                enabled: true,
                styleVersions: styleVersions
            };
            
            // Add condition-specific data
            if (conditionType === 'distance') {
                badgeData.distanceThreshold = distanceThreshold * 1000; // Convert to meters
                if (activityType) {
                    badgeData.activityType = activityType;
                }
            } else if (conditionType === 'location') {
                badgeData.locationCity = locationCity.trim();
                badgeData.locationState = locationState.trim();
                badgeData.locationCountry = locationCountry.trim();
            }
            
            if (editingBadgeId) {
                // Update existing badge
                await updateDoc(doc(db, 'badges', editingBadgeId), {
                    ...badgeData,
                    updatedAt: new Date()
                });
                showNotification('Badge updated successfully!');
            } else {
                // Create new badge
                await addDoc(collection(db, 'badges'), {
                    ...badgeData,
                    createdAt: new Date()
                });
                showNotification('Badge created successfully!');
            }
            
            // Reset form
            handleCancelEdit();
            
            // Reload badges
            await loadBadges();
        } catch (error) {
            console.error('Error saving badge:', error);
            showNotification(`Failed to ${editingBadgeId ? 'update' : 'create'} badge. Please try again.`, 'error');
        } finally {
            setIsUploading(false);
        }
    };
    
    const handleToggleBadge = async (badgeId, currentEnabled) => {
        try {
            await updateDoc(doc(db, 'badges', badgeId), {
                enabled: !currentEnabled
            });
            await loadBadges();
        } catch (error) {
            console.error('Error toggling badge:', error);
        }
    };
    
    const handleDeleteBadge = async (badgeId) => {
        if (window.confirm('Are you sure you want to delete this badge?')) {
            try {
                await deleteDoc(doc(db, 'badges', badgeId));
                await loadBadges();
            } catch (error) {
                console.error('Error deleting badge:', error);
            }
        }
    };
    
    const handleStyleImageChange = async (styleName, file) => {
        if (!file) return;
        
        try {
            const timestamp = Date.now();
            const filename = `badges/style_versions/${timestamp}_${styleName}_${file.name}`;
            const storageRef = ref(storage, filename);
            await uploadBytes(storageRef, file);
            const imageUrl = await getDownloadURL(storageRef);
            
            setStyleVersions(prev => ({
                ...prev,
                [styleName]: {
                    ...prev[styleName],
                    imageUrl
                }
            }));
            
            showNotification(`Style version image uploaded for ${styleName}`);
        } catch (error) {
            console.error('Error uploading style image:', error);
            showNotification('Failed to upload style image', 'error');
        }
    };
    
    const handleStyleSizeChange = (styleName, size) => {
        setStyleVersions(prev => ({
            ...prev,
            [styleName]: {
                ...prev[styleName],
                size: parseFloat(size)
            }
        }));
    };
    
    const handleRemoveStyleVersion = (styleName) => {
        setStyleVersions(prev => {
            const newVersions = { ...prev };
            delete newVersions[styleName];
            return newVersions;
        });
    };
    
    const formatCondition = (badge) => {
        if (badge.conditionType === 'distance') {
            let condition = `Distance > ${(badge.distanceThreshold / 1000).toFixed(0)} km`;
            if (badge.activityType) {
                const activityLabel = badge.activityType === 'ride' ? 'Rides' : 'Runs';
                condition += ` (${activityLabel} only)`;
            }
            return condition;
        } else if (badge.conditionType === 'location') {
            const parts = [];
            if (badge.locationCity) parts.push(`City: ${badge.locationCity}`);
            if (badge.locationState) parts.push(`State: ${badge.locationState}`);
            if (badge.locationCountry) parts.push(`Country: ${badge.locationCountry}`);
            return parts.join(', ');
        }
        return 'Unknown condition';
    };
    
    return (
        <div className="badges-admin-page">
            {notification.show && (
                <div className={`notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}
            
            <div className="page-header">
                <h1>Badge Management</h1>
                <p>Create and manage badges that are automatically awarded based on activity conditions</p>
            </div>
            
            <div className="badge-creation-section">
                <h2>{editingBadgeId ? 'Edit Badge' : 'Create New Badge'}</h2>
                <form onSubmit={handleSubmit} className="badge-form">
                    <div className="form-group">
                        <label htmlFor="badgeName">Badge Name</label>
                        <input
                            type="text"
                            id="badgeName"
                            value={badgeName}
                            onChange={(e) => setBadgeName(e.target.value)}
                            placeholder="e.g., Century Rider"
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="badgeImage">Badge Image {editingBadgeId && '(leave empty to keep current image)'}</label>
                        <input
                            type="file"
                            id="badgeImage"
                            accept="image/*"
                            onChange={handleImageChange}
                            required={!editingBadgeId}
                        />
                        {badgeImagePreview && (
                            <div className="image-preview">
                                <img src={badgeImagePreview} alt="Badge preview" />
                            </div>
                        )}
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="badgeSize">Badge Size (% of map width)</label>
                        <input
                            type="number"
                            id="badgeSize"
                            value={badgeSize}
                            onChange={(e) => setBadgeSize(parseFloat(e.target.value))}
                            min="1"
                            max="20"
                            step="0.5"
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="conditionType">Award Condition</label>
                        <select
                            id="conditionType"
                            value={conditionType}
                            onChange={(e) => setConditionType(e.target.value)}
                        >
                            <option value="distance">Distance Based</option>
                            <option value="location">Location Based</option>
                        </select>
                    </div>
                    
                    {conditionType === 'distance' && (
                        <>
                            <div className="form-group">
                                <label htmlFor="distanceThreshold">Minimum Distance (km)</label>
                                <input
                                    type="number"
                                    id="distanceThreshold"
                                    value={distanceThreshold}
                                    onChange={(e) => setDistanceThreshold(parseFloat(e.target.value))}
                                    min="1"
                                    step="1"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="activityType">Activity Type (optional)</label>
                                <select
                                    id="activityType"
                                    value={activityType}
                                    onChange={(e) => setActivityType(e.target.value)}
                                >
                                    <option value="">All Activities</option>
                                    <option value="ride">Rides (Cycling, E-Bike, Virtual Ride)</option>
                                    <option value="run">Runs (Running, Walking, Hiking)</option>
                                </select>
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    If selected, badge will only be awarded for the specified activity type.
                                </p>
                            </div>
                        </>
                    )}
                    
                    {conditionType === 'location' && (
                        <>
                            <div className="form-group">
                                <label htmlFor="locationCity">Cities (comma-separated, optional)</label>
                                <input
                                    type="text"
                                    id="locationCity"
                                    value={locationCity}
                                    onChange={(e) => setLocationCity(e.target.value)}
                                    placeholder="e.g., San Francisco, Los Angeles, New York"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="locationState">States/Regions (comma-separated, optional)</label>
                                <input
                                    type="text"
                                    id="locationState"
                                    value={locationState}
                                    onChange={(e) => setLocationState(e.target.value)}
                                    placeholder="e.g., California, Texas, New York"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="locationCountry">Countries (comma-separated, optional)</label>
                                <input
                                    type="text"
                                    id="locationCountry"
                                    value={locationCountry}
                                    onChange={(e) => setLocationCountry(e.target.value)}
                                    placeholder="e.g., United States, Canada, Mexico"
                                />
                            </div>
                            <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                If multiple fields are filled, all must match for the badge to be awarded.
                            </p>
                        </>
                    )}
                    
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={showStyleVersions}
                                onChange={(e) => setShowStyleVersions(e.target.checked)}
                            />
                            Add Style-Specific Versions
                        </label>
                    </div>
                    
                    {showStyleVersions && (
                        <div className="style-versions-section">
                            <h3>Style-Specific Badge Versions</h3>
                            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                                Upload different badge images for specific map styles. If a style-specific version is not provided, the default badge image will be used.
                            </p>
                            
                            {availableStyles.filter(style => style.id !== 'default').map(style => (
                                <div key={style.id} className="style-version-item" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                                    <h4>{style.name}</h4>
                                    <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <label>Badge Image for {style.name}</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => e.target.files[0] && handleStyleImageChange(style.name, e.target.files[0])}
                                            />
                                            {styleVersions[style.name]?.imageUrl && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <img 
                                                        src={styleVersions[style.name].imageUrl} 
                                                        alt={`${style.name} badge`}
                                                        style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label>Size (% of map)</label>
                                            <input
                                                type="number"
                                                value={styleVersions[style.name]?.size || badgeSize}
                                                onChange={(e) => handleStyleSizeChange(style.name, e.target.value)}
                                                min="1"
                                                max="20"
                                                step="0.5"
                                                style={{ width: '80px' }}
                                            />
                                        </div>
                                        {styleVersions[style.name] && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveStyleVersion(style.name)}
                                                style={{ marginTop: '22px', padding: '5px 10px', backgroundColor: '#ff4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className="form-actions">
                        <button type="submit" disabled={isUploading} className="submit-button">
                            {isUploading ? (editingBadgeId ? 'Updating Badge...' : 'Creating Badge...') : (editingBadgeId ? 'Update Badge' : 'Create Badge')}
                        </button>
                        {editingBadgeId && (
                            <button type="button" onClick={handleCancelEdit} className="cancel-button">
                                Cancel Edit
                            </button>
                        )}
                    </div>
                </form>
            </div>
            
            <div className="existing-badges-section">
                <h2>Existing Badges</h2>
                {isLoading ? (
                    <p>Loading badges...</p>
                ) : badges.length === 0 ? (
                    <p>No badges created yet.</p>
                ) : (
                    <div className="badges-grid">
                        {badges.map(badge => (
                            <div key={badge.id} className={`badge-card ${!badge.enabled ? 'disabled' : ''}`}>
                                <div className="badge-images-container">
                                    <div className="badge-image main-badge">
                                        <img src={badge.imageUrl} alt={badge.name} />
                                        <div className="badge-size-indicator">{badge.size}%</div>
                                        <div className="badge-style-label">Default</div>
                                    </div>
                                    {badge.styleVersions && Object.entries(badge.styleVersions).map(([styleName, styleData]) => (
                                        <div key={styleName} className="badge-image style-variant">
                                            <img src={styleData.imageUrl} alt={`${badge.name} - ${styleName}`} />
                                            <div className="badge-size-indicator">{styleData.size || badge.size}%</div>
                                            <div className="badge-style-label">{styleName}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="badge-info">
                                    <h3>{badge.name}</h3>
                                    <p className="badge-condition">{formatCondition(badge)}</p>
                                </div>
                                <div className="badge-actions">
                                    <button
                                        onClick={() => handleEditBadge(badge)}
                                        className="edit-button"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleToggleBadge(badge.id, badge.enabled)}
                                        className={`toggle-button ${badge.enabled ? 'enabled' : 'disabled'}`}
                                    >
                                        {badge.enabled ? 'Enabled' : 'Disabled'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteBadge(badge.id)}
                                        className="delete-button"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BadgesAdminPage;